import {
  envelopeItemTypeToDataCategory,
  forEachEnvelopeItem,
  serializeEnvelope,
} from "@sentry/core";
import type { Client, TransactionEvent, Transport } from "@sentry/core";

// Sentry rejects error, transaction, span, and metric envelope items above 1 MB.
// Leave room for envelope headers and any SDK metadata added after the event hook.
export const SENTRY_TRANSACTION_BYTE_BUDGET = 750_000;
export const SENTRY_ENVELOPE_BYTE_BUDGET = 900_000;

const HANIVERSE_QUERY_HASH_ATTRIBUTE = "han.query.hash";
const HANIVERSE_QUERY_SPAN_OP = "han.query";

export function sentrySerializedBodyByteLength(body: string | Uint8Array): number {
  return typeof body === "string" ? new TextEncoder().encode(body).byteLength : body.byteLength;
}

function jsonByteLength(value: unknown): number {
  try {
    const serialized = JSON.stringify(value);
    return serialized === undefined ? 0 : sentrySerializedBodyByteLength(serialized);
  } catch {
    // A processed Sentry event should already be serializable. Treat an unexpected
    // serialization failure as over-budget so it cannot escape the final guard.
    return Number.POSITIVE_INFINITY;
  }
}

function withoutQueryHash(
  span: NonNullable<TransactionEvent["spans"]>[number],
): NonNullable<TransactionEvent["spans"]>[number] {
  if (span.data === undefined || !(HANIVERSE_QUERY_HASH_ATTRIBUTE in span.data)) {
    return span;
  }

  const data = { ...span.data };
  Reflect.deleteProperty(data, HANIVERSE_QUERY_HASH_ATTRIBUTE);
  return { ...span, data };
}

function dropLargestMatchingSpans(
  event: TransactionEvent,
  maxBytes: number,
  matches: (span: NonNullable<TransactionEvent["spans"]>[number]) => boolean,
): TransactionEvent {
  const spans = event.spans;
  if (spans === undefined || spans.length === 0) {
    return event;
  }

  const bytesToRemove = jsonByteLength(event) - maxBytes;
  if (bytesToRemove <= 0) {
    return event;
  }

  const candidates = spans
    .map((span, index) => ({
      bytes: jsonByteLength(span),
      index,
      span,
    }))
    .filter(({ span }) => matches(span))
    .sort((left, right) => right.bytes - left.bytes || right.index - left.index);

  const indexesToDrop = new Set<number>();
  let removedBytes = 0;
  for (const candidate of candidates) {
    indexesToDrop.add(candidate.index);
    // A removed array item also removes one comma except when it was the only item.
    // The exact size is checked again after filtering, so this is only a selection estimate.
    removedBytes += candidate.bytes + 1;
    if (removedBytes >= bytesToRemove) {
      break;
    }
  }

  if (indexesToDrop.size === 0) {
    return event;
  }

  return {
    ...event,
    spans: spans.filter((_span, index) => !indexesToDrop.has(index)),
  };
}

/**
 * Bounds a transaction before the SDK turns it into an envelope.
 *
 * Query spans are the first trimming priority because a single navigation can
 * generate hundreds of them. Non-query spans are retained unless query spans
 * alone cannot bring the transaction below budget. Returning null is the final
 * fallback for an event whose non-span fields are themselves too large.
 */
export function trimSentryTransactionToByteLimit(
  event: TransactionEvent,
  maxBytes: number,
): TransactionEvent | null {
  let trimmedEvent: TransactionEvent =
    event.spans === undefined
      ? event
      : { ...event, spans: event.spans.map((span) => withoutQueryHash(span)) };

  if (jsonByteLength(trimmedEvent) <= maxBytes) {
    return trimmedEvent;
  }

  trimmedEvent = dropLargestMatchingSpans(
    trimmedEvent,
    maxBytes,
    (span) => span.op === HANIVERSE_QUERY_SPAN_OP,
  );
  if (jsonByteLength(trimmedEvent) <= maxBytes) {
    return trimmedEvent;
  }

  trimmedEvent = dropLargestMatchingSpans(trimmedEvent, maxBytes, () => true);
  return jsonByteLength(trimmedEvent) <= maxBytes ? trimmedEvent : null;
}

export function trimSentryTransaction(event: TransactionEvent): TransactionEvent | null {
  return trimSentryTransactionToByteLimit(event, SENTRY_TRANSACTION_BYTE_BUDGET);
}

/**
 * Last-resort protection around Sentry's normal transport. Transaction hooks
 * preserve as much data as possible; this guard guarantees that no unexpectedly
 * large event, log batch, or other envelope reaches the network.
 */
export function withSentryEnvelopeSizeLimit(
  transport: Transport,
  recordDroppedEvent: Client["recordDroppedEvent"],
  maxBytes: number = SENTRY_ENVELOPE_BYTE_BUDGET,
): Transport {
  return {
    flush(timeout) {
      return transport.flush(timeout);
    },
    send(envelope) {
      const body = serializeEnvelope(envelope);
      if (sentrySerializedBodyByteLength(body) <= maxBytes) {
        return transport.send(envelope);
      }

      forEachEnvelopeItem(envelope, (_item, type) => {
        recordDroppedEvent("before_send", envelopeItemTypeToDataCategory(type));
      });
      return Promise.resolve({});
    },
  };
}
